/**
 * Leads repository — Prisma-first (NestJS is the system of record).
 *
 * This domain is Nest-only: there is no `public.leads` / `public.interests`
 * Supabase table (the `app.leads` / `app.interests` tables are owned by the
 * NestJS slice). Each method requires VITE_API_URL to be configured — if it is
 * null, `makeNestRequest` throws a clear config error so the caller surfaces the
 * misconfiguration instead of silently returning nothing.
 *
 * Pattern: mirrors `src/repositories/brokerageContracts.ts` — bind the slice
 * once via `makeNestRequest`, resolve paths from `API_ROUTES`, and `.parse()`
 * every response against the shared Zod schema at the flip boundary so any
 * backend/frontend drift surfaces at runtime.
 *
 * Interests are always nested under a lead, so their methods take a `leadId`.
 * Update is PATCH (the Nest controllers use @Patch — partial replacement).
 *
 * See: docs/manifests/manifest-6-supabase-to-nestjs.md
 */
import { makeNestRequest } from "@/services/nestRequest";
import { API_ROUTES } from "@/api/routes";
import {
  leadSchema,
  interestSchema,
  leadByTaskResult,
  publicInterestCaptureResult,
  ownerBookingResult,
  myInterestsResult,
} from "@dallali/shared";
import { z } from "zod";

import type {
  Lead,
  Interest,
  LeadByTaskResult,
  MyInterestsResult,
  CreateLeadInput,
  UpdateLeadInput,
  CreateInterestInput,
  UpdateInterestInput,
  PublicInterestCaptureInput,
  PublicInterestCaptureResult,
  OwnerBookingInput,
  OwnerBookingResult,
} from "@dallali/shared";

/** POST /leads/public/request-otp response (same shape as /auth/request-otp). */
const publicCaptureOtpResult = z
  .object({ success: z.boolean(), message: z.string().optional() })
  .passthrough();

// Result of POST /leads/merge (S7). The endpoint returns the Nest MergeResult
// shape verbatim (camelCase, not a serialized Lead) — there is no shared schema
// for it yet, so the contract is pinned locally and parsed at the flip boundary.
const leadMergeResultSchema = z.object({
  survivingLeadId: z.string().nullable(),
  merged: z.boolean(),
  foldedInterests: z.number(),
});
export type LeadMergeResult = z.infer<typeof leadMergeResultSchema>;

// Result of POST /leads/self — whether a landing lead was just created + its id.
const ensureSelfResultSchema = z.object({
  created: z.boolean(),
  leadId: z.string(),
});
export type EnsureSelfResult = z.infer<typeof ensureSelfResultSchema>;

// Authenticated Nest JSON request bound to the "leads" slice — shared transport
// (timeout + retry + typed NestRequestError). See src/services/nestRequest.ts.
const nestRequest = makeNestRequest("leads");

// UNAUTHENTICATED Nest request for the anonymous public capture endpoint (S5).
// `auth: false` → no session lookup, no Authorization header (the caller is an
// anonymous visitor on a public ad listing page).
const nestPublicRequest = makeNestRequest("leads public", { auth: false });

export const leadsRepository = {
  // ── Leads ──────────────────────────────────────────────────────────────────

  /**
   * GET /leads
   * Returns every lead for the authenticated company (the guarded endpoint
   * scopes by JWT company; no query-param filtering server-side).
   * Requires VITE_API_URL.
   */
  async list(): Promise<Lead[]> {
    const res = await nestRequest<unknown>(API_ROUTES.leads.list());
    return z.array(leadSchema).parse(res);
  },

  /**
   * GET /leads/:id — single lead. Cross-tenant → 404. Requires VITE_API_URL.
   */
  async getById(id: string): Promise<Lead> {
    const res = await nestRequest<unknown>(API_ROUTES.leads.getById(id));
    return leadSchema.parse(res);
  },

  /**
   * GET /leads/by-task/:taskId — the lead that spawned a given kanban task plus
   * its interests (each with the resolved source listing). Used by the Task
   * modal's "Lead details" section. A task with no linked lead returns 200 +
   * null — the caller treats null as "not a lead-spawned task" and renders
   * nothing. Requires VITE_API_URL.
   */
  async getByTask(taskId: string): Promise<LeadByTaskResult | null> {
    const res = await nestRequest<unknown>(API_ROUTES.leads.byTask(taskId));
    // A task with no linked lead may come back as either an explicit null body
    // or an empty 200 (which the transport now normalises to undefined).
    if (res === null || res === undefined) return null;
    return leadByTaskResult.parse(res);
  },

  /**
   * POST /leads (JSON) — creates a lead. source/full_name/phone required;
   * company_id + audit columns are derived server-side. Requires VITE_API_URL.
   */
  async create(payload: CreateLeadInput): Promise<Lead> {
    const res = await nestRequest<unknown>(API_ROUTES.leads.create(), {
      method: "POST",
      body: JSON.stringify(payload),
    });
    return leadSchema.parse(res);
  },

  /**
   * POST /leads/owner-interest (JSON) — owner-portal self-service capture (S6).
   * AUTH-ONLY (no service_requests.create permission needed); `source` is
   * server-pinned to owner_portal and `userId` is linked to the signed-in owner.
   * Requires VITE_API_URL.
   */
  async createOwnerInterest(payload: CreateLeadInput): Promise<Lead> {
    const res = await nestRequest<unknown>(API_ROUTES.leads.ownerInterest(), {
      method: "POST",
      body: JSON.stringify(payload),
    });
    return leadSchema.parse(res);
  },

  /**
   * POST /leads/owner-booking (JSON, AUTH) — authenticated owner-portal project
   * booking. The server pins source=owner_portal, userId, intents=['buy'],
   * use='residential', and derives city from the project — the FE must NOT send
   * those fields. Returns the same minimal confirmation as createPublicInterest.
   * Requires VITE_API_URL and a valid JWT (SupabaseAuthGuard).
   */
  async createOwnerBooking(
    payload: OwnerBookingInput,
  ): Promise<OwnerBookingResult> {
    const res = await nestRequest<unknown>(API_ROUTES.leads.ownerBooking(), {
      method: "POST",
      body: JSON.stringify(payload),
    });
    return ownerBookingResult.parse(res);
  },

  /**
   * POST /leads/merge — anon→known lead merge (S7). Call once post-login: folds
   * any anonymous leads sharing the caller's verified phone onto their user.
   * Idempotent (`merged:false` when there was nothing to fold). AUTH-ONLY; the
   * phone is resolved server-side from the caller's profile. Requires VITE_API_URL.
   */
  async merge(): Promise<LeadMergeResult> {
    const res = await nestRequest<unknown>(API_ROUTES.leads.merge(), {
      method: "POST",
    });
    return leadMergeResultSchema.parse(res);
  },

  /**
   * POST /leads/self — ensure the signed-in user is in the Leads Address Book
   * (Epic L: every owner registration becomes a lead). Idempotently creates a
   * source=landing lead if they don't own one. Auth-only; phone/name resolved
   * server-side. Call on owner-portal mount (covers signup + login + backfill).
   */
  async ensureSelf(): Promise<EnsureSelfResult> {
    const res = await nestRequest<unknown>(API_ROUTES.leads.self(), {
      method: "POST",
    });
    return ensureSelfResultSchema.parse(res);
  },

  /**
   * GET /leads/mine — the signed-in user's own leads + interests (owner "My
   * Interests" history). Auth-only; scoped server-side by the caller's user id.
   */
  async listMine(): Promise<MyInterestsResult> {
    const res = await nestRequest<unknown>(API_ROUTES.leads.mine());
    return myInterestsResult.parse(res);
  },

  /**
   * GET /leads/assigned — the leads tied to TASKS assigned to the calling
   * employee (employee portal "My Leads"). Auth-only; the employee is resolved
   * server-side from the JWT. Each row carries only the interests whose spawned
   * task belongs to this employee. Same shape as `listMine`. Requires VITE_API_URL.
   */
  async listAssignedToMe(): Promise<MyInterestsResult> {
    const res = await nestRequest<unknown>(API_ROUTES.leads.assigned());
    return myInterestsResult.parse(res);
  },

  /**
   * PATCH /leads/:id (JSON) — partial update, all fields optional.
   * Requires VITE_API_URL.
   */
  async update(id: string, payload: UpdateLeadInput): Promise<Lead> {
    const res = await nestRequest<unknown>(API_ROUTES.leads.update(id), {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
    return leadSchema.parse(res);
  },

  /**
   * DELETE /leads/:id — soft-delete (204 No Content). Requires VITE_API_URL.
   */
  async remove(id: string): Promise<void> {
    await nestRequest<void>(API_ROUTES.leads.remove(id), { method: "DELETE" });
  },

  // ── Interests (nested under a lead) ──────────────────────────────────────────

  /**
   * GET /leads/:leadId/interests — every interest for the given lead.
   * Requires VITE_API_URL.
   */
  async listInterests(leadId: string): Promise<Interest[]> {
    const res = await nestRequest<unknown>(
      API_ROUTES.leads.interests.list(leadId),
    );
    return z.array(interestSchema).parse(res);
  },

  /**
   * GET /leads/:leadId/interests/:id — single interest. Requires VITE_API_URL.
   */
  async getInterestById(leadId: string, id: string): Promise<Interest> {
    const res = await nestRequest<unknown>(
      API_ROUTES.leads.interests.getById(leadId, id),
    );
    return interestSchema.parse(res);
  },

  /**
   * POST /leads/:leadId/interests (JSON) — creates an interest under the lead.
   * use/intents(≥1)/city required; the service enforces priceMin ≤ priceMax and
   * sizeMin ≤ sizeMax. Requires VITE_API_URL.
   */
  async createInterest(
    leadId: string,
    payload: CreateInterestInput,
  ): Promise<Interest> {
    const res = await nestRequest<unknown>(
      API_ROUTES.leads.interests.create(leadId),
      { method: "POST", body: JSON.stringify(payload) },
    );
    return interestSchema.parse(res);
  },

  /**
   * PATCH /leads/:leadId/interests/:id (JSON) — partial update, all optional.
   * Requires VITE_API_URL.
   */
  async updateInterest(
    leadId: string,
    id: string,
    payload: UpdateInterestInput,
  ): Promise<Interest> {
    const res = await nestRequest<unknown>(
      API_ROUTES.leads.interests.update(leadId, id),
      { method: "PATCH", body: JSON.stringify(payload) },
    );
    return interestSchema.parse(res);
  },

  /**
   * DELETE /leads/:leadId/interests/:id — soft-delete (204). Requires VITE_API_URL.
   */
  async removeInterest(leadId: string, id: string): Promise<void> {
    await nestRequest<void>(API_ROUTES.leads.interests.remove(leadId, id), {
      method: "DELETE",
    });
  },

  // ── Public anonymous capture (Epic L / S5) ────────────────────────────────────

  /**
   * POST /leads/public/request-otp (JSON, UNAUTHENTICATED) — send the SMS code
   * the public capture verifies. Accepts any supported-country mobile in E.164
   * (`PHONE_COUNTRIES`); never logs the visitor in. Requires VITE_API_URL.
   */
  async requestPublicCaptureOtp(phone: string): Promise<void> {
    const res = await nestPublicRequest<unknown>(API_ROUTES.leads.publicCaptureOtp(), {
      method: "POST",
      body: JSON.stringify({ phone }),
    });
    // The issuer answers 2xx with { success: false } when the SMS provider
    // refused/was unreachable — surface that as a failure, not "code sent".
    const parsed = publicCaptureOtpResult.parse(res);
    if (!parsed.success) throw new Error(parsed.message || "Failed to send verification code");
  },

  /**
   * POST /leads/public (JSON, UNAUTHENTICATED) — register interest from a public
   * ad listing page. company_id is derived server-side from the listing — never
   * sent. Re-submitting the same phone reuses the existing lead (dedupe). Returns
   * a minimal confirmation (lead/interest ids + dedupe flag). Requires VITE_API_URL.
   */
  async createPublicInterest(
    payload: PublicInterestCaptureInput,
  ): Promise<PublicInterestCaptureResult> {
    const res = await nestPublicRequest<unknown>(
      API_ROUTES.leads.publicCapture(),
      { method: "POST", body: JSON.stringify(payload) },
    );
    return publicInterestCaptureResult.parse(res);
  },
};
